import less from 'less';
import flowRight from 'lodash/flowRight';
import isString from 'lodash/isString';
import map from 'lodash/map';
import partial from 'lodash/partial';
import template from 'underscore/modules/template.js';
import { get as gcsGet } from '@/data/gcs-service';

const RootTemplateVariable = 'root';

const scriptHeader = '/*BEGIN PENDO SCRIPT WRAPPER*/\n';
const scriptFooter = '\n/*END PENDO SCRIPT WRAPPER*/';
const inlineScriptHeader = `${'\n' + // does not need root var reference, since this is already for compatibility anyway
    "<% if (typeof guide !== 'undefined') { %>\n" +
    "var guide = pendo.findGuideById('<%= guide.id %>');\n" +
    "var step = guide && guide.findStepById('<%= step.id %>');\n" +
    '<% } %>\n'}${scriptHeader}`;
const inlineScriptFooter = scriptFooter;
const openScriptTag = '<script id="pendo-inline-script">';
const closeScriptTag = '</script>';
const openStyleTag = '<style id="pendo-inline-css" type="text/css">';
const closeStyleTag = '</style>';

/**
 * Removes the style and script blocks from guide content
 * @param {string} combinedContent The guide content
 * @type {string} The content with the style and script blocks removed
 */
const stripStyleAndScript = flowRight(
    partial(stripContentBetween, openScriptTag, closeScriptTag),
    partial(stripContentBetween, openStyleTag, closeStyleTag),
);

const regex = {
    get rootVariable () {
        return new RegExp(`(<%[^%]*?)${RootTemplateVariable}\\.([^%]*%>)`, 'g');
    },

    get templateExpression () {
        return /<%[^%]*%>/g;
    },
};

/**
 * Loads the content for a step
 * @param  {GuideStep} step
 * @return {Promise.<String>} Promise that returns the step content as a string
 */

/* eslint-disable no-nested-ternary*/
export function getStepContent (step) {
    if (!step) return Promise.resolve('');

    return Promise.resolve(
        step.content ? step.content : step.contentUrl ? gcsGet(step.contentUrl, { cache: true }) : step.content,
    );
}

/**
 * @typedef {Object} ExplodedContent
 * @property {string}           html
 * @property {string}           css
 * @property {string}           javascript
 * @property {string|undefined} cssPrefix The CSS rule to wrap all styles with
 */

/**
 * Combines HTML, CSS, and Javascript. Used to support older
 * agent versions and for previews in the designer.
 * @param  {ExplodedContent} explodedContent
 * @return {Promise.<String>}
 */
export function combineContent (explodedContent) {
    return scopeCss(explodedContent.cssPrefix, explodedContent.css).then((css) => {
        const builder = [commentRootVariable(explodedContent.html)];

        if (css) {
            builder.push(openStyleTag, css, closeStyleTag);
        }

        if (explodedContent.javascript) {
            builder.push(
                openScriptTag,
                inlineScriptHeader,
                explodedContent.javascript,
                inlineScriptFooter,
                closeScriptTag,
            );
        }

        return builder.join('');
    });
}

/**
 * Removes smart quotes from content.
 * @param  {String} content
 * @return {String}
 */
export function removeSmartQuotes (content) {
    const smartQuoteRegex = /(‘|‛|’|‚|′|“|”|„|‟|″)/g;

    const matchMap = {
        '‘': "'",
        '‛': "'",
        '’': "'",
        '‚': "'",
        '′': "'",
        '“': '"',
        '”': '"',
        '„': '"',
        '‟': '"',
        '″': '"',
    };

    return content.replace(smartQuoteRegex, (match) => {
        return matchMap[match];
    });
}

/**
 * Generates the CSS prefix for a step
 * @param  {GuideStep} step
 * @return {String}
 */
export function stepCssPrefix (step) {
    return `#_pendo_g_${step.id}`;
}

export function p2StepCssPrefix (step) {
    return `#pendo-g-${step.id}`;
}

export function usesRootTemplateVariable (html) {
    // No template delimiters means we're okay to use the root variable
    if (!/<%[^%]+%>/g.test(html)) return true;

    return regex.rootVariable.test(html);
}

export function uncommentRootVariable (html) {
    if (!isString(html)) return html;

    return html.replace(regex.templateExpression, (match) => {
        return match.replace(new RegExp(`/\\*${RootTemplateVariable}\\.\\*/`, 'g'), `${RootTemplateVariable}.`);
    });
}

export function commentRootVariable (html) {
    if (!isString(html)) return html;

    return html.replace(regex.templateExpression, (match) => {
        return uncommentRootVariable(match) // prevent double commenting
            .replace(new RegExp(`${RootTemplateVariable}\\.`, 'g'), `/*${RootTemplateVariable}.*/`);
    });
}

/**
 * Splits combined content into HTML, CSS, and Javascript components
 * @param  {String} combinedContent
 * @return {ExplodedContent}
 */
export function splitContent (combinedContent) {
    const html = uncommentRootVariable(stripStyleAndScript(combinedContent));
    const css = extractContent(combinedContent, openStyleTag, closeStyleTag);
    let javascript = extractContent(combinedContent, scriptHeader, scriptFooter);
    if (!javascript) {
        // Handle older content that is not wrapped yet
        const parsed = new DOMParser().parseFromString(combinedContent, 'text/html');
        const inlineScript = parsed.querySelector('#pendo-inline-script');
        if (inlineScript) {
            javascript = inlineScript.textContent;
        }
    }
    const result = { html };
    if (css) result.css = css;
    if (javascript) result.javascript = javascript;

    return result;
}

/**
 * Removes everything in a string based on given start and end delimiters.
 * @param  {string} contentStart The start delimiter
 * @param  {string} contentEnd   The end delimiter
 * @param  {string} stepContent  The string from which to remove content
 * @return {string}              The string with content removed (assuming the start/end were matched)
 */

/* eslint-disable no-cond-assign*/
function stripContentBetween (contentStart, contentEnd, stepContent) {
    if (stepContent != null) {
        let start;
        let end;
        while ((start = stepContent.indexOf(contentStart)) >= 0) {
            end = stepContent.indexOf(contentEnd, start) + contentEnd.length;
            while (stepContent[end] === '\n') {
                end++;
            }
            stepContent = stepContent.substring(0, start) + stepContent.substring(end);
        }
    }

    return stepContent;
}

/**
 * Extracts content between a given header and footer
 * @param  {String} combinedContent
 * @param  {String} header
 * @param  {String} footer
 * @return {String} The extracted content
 */
function extractContent (combinedContent, header, footer) {
    const start = combinedContent.indexOf(header);
    const end = combinedContent.lastIndexOf(footer);

    if (start < 0 || end <= start + header.length) return;

    return combinedContent.slice(start + header.length, end);
}

/**
 * @typedef {Object} CspContent
 * @property {String|undefined} contentCss The scoped css
 * @property {String}           contentJs The html and javascript packaged in a pendo.guideContent callback
 */

/**
 * Generates CSP-compatible content from the "inlined" version
 * of the guide content.
 * @param  {GuideStep} step The step... or anything with guideId and id properties
 * @param  {string} combinedContent
 * @return {CspContent}
 */
export function generateCspContent (step, combinedContent) {
    const result = {
        contentCss: '',
        contentJs: '',
    };

    if (!combinedContent) {
        return result;
    }

    let { html, javascript, css } = splitContent(combinedContent);
    html = uncommentRootVariable(html);

    const templatePattern = /<%[^%]*%>/;
    if ((css && templatePattern.test(css)) || (javascript && templatePattern.test(javascript))) {
        // Do not generate CSP compatible content if there are template parameters in script or css
        return result;
    }

    const guideHtml = new DOMParser().parseFromString(html, 'text/html');
    if (guideHtml.querySelectorAll('[onclick]').length) {
        // Do not generate CSP content if there are onclick attributes in the html
        return result;
    }

    if (css) {
        result.contentCss = css;
    }

    const templateString = JSON.stringify(combinedContent);
    const templateOptions = usesRootTemplateVariable(html) ? { variable: RootTemplateVariable } : null;
    const templateFunction = template(html, templateOptions).source;
    javascript = javascript || '';
    result.contentJs = `pendo.guideContent('${step.guideId}','${step.id}',${templateString},${templateFunction},function(step,guide){${javascript}});`;

    return result;
}

/**
 * Uses LESS to scope a block of CSS to a given selector.
 * @param  {string} prefix     The selector to prepend to every rule in the CSS block
 * @param  {string} lessSource The CSS block
 * @return {string}            The scoped CSS block
 */
function scopeCss (prefix, lessSource) {
    return lessSource
        ? less.render(`${prefix} { ${lessSource} }`).then((lessResult) => {
            return lessResult.css;
        })
        : Promise.resolve();
}

/**
 * Load the content for a single {step} into the `content` property
 * @param {GuideStep} step
 * @return {Promise.<GuideStep>} fulfilled after content is loaded
 */
export function loadStepContent (step) {
    if (!step) return Promise.resolve('');

    return getStepContent(step).then((content) => {
        step.content = content;

        return step;
    });
}

/**
 * Loads the content for _all steps_ into a guide
 *
 * @param {Guide} guide
 * @return {Promise.<Guide>} fulfilled when all content is loaded
 */
export function loadGuideContent (guide) {
    return Promise.all(map(guide.steps || [], loadStepContent)).then(() => {
        return guide;
    });
}

export function prepareContentForPreview (content) {
    return commentRootVariable(uncommentRootVariable(content));
}

export function escapeForSrcdoc (str) {
    return str.replace(/<\//g, '<\\/');
}

// Start scripts used to render code block previews in app
function getDataVars (domJson, codeBlockContent, codeBlockCss) {
    return [
        `var stepDom = ${domJson};`,
        `var codeBlockContent = ${escapeForSrcdoc(JSON.stringify(codeBlockContent))};`,
        `var codeBlockCss = ${escapeForSrcdoc(JSON.stringify(codeBlockCss))};`,
    ];
}

function getMockGlobals (stepId, guideId) {
    return [
        `var guideId = ${guideId};`,
        '',
        'var step = {',
        `  id: ${JSON.stringify(stepId)},`,
        '  guideId: guideId,',
        '  getContent: function() { return codeBlockContent; },',
        '  attachEvent: function() {},',
        '  advance: function() {},',
        '  dismiss: function() {},',
        '  response: function() {},',
        '  eventRouter: { eventable: { trigger: function() {} } },',
        '};',
        '',
        'var guide = {',
        '  id: guideId,',
        '  findStepById: function() { return step; },',
        '  steps: [step],',
        '};',
        '',
        'window.step = step;',
        'window.guide = guide;',
    ];
}

const renderDom = [
    'var renderedNode = window.pendo.buildNodeFromJSON(stepDom, step, []);',
    'if (!renderedNode || !renderedNode[0]) return;',
    '',
    'document.body.appendChild(renderedNode[0]);',
    '',
    'if (codeBlockCss) {',
    '  var styleEl = document.createElement("style");',
    '  styleEl.setAttribute("data-code-block-css", "");',
    '  styleEl.textContent = codeBlockCss;',
    '  document.head.appendChild(styleEl);',
    '}',
];

const overridePositioning = [
    'var guideContainer = document.querySelector("[id^=\\"pendo-g-\\"]");',
    'if (guideContainer) {',
    '  guideContainer.style.setProperty("position", "relative", "important");',
    '  guideContainer.style.setProperty("top", "auto", "important");',
    '  guideContainer.style.setProperty("bottom", "auto", "important");',
    '  guideContainer.style.setProperty("left", "0", "important");',
    '  guideContainer.style.setProperty("right", "0", "important");',
    '  guideContainer.style.setProperty("margin-left", "auto", "important");',
    '  guideContainer.style.setProperty("margin-right", "auto", "important");',
    '}',
];

const patchPendoApi = [
    'if (window.pendo) {',
    '  window.pendo.findGuideById = function() { return guide; };',
    '  window.pendo.onGuidePrevious = function() {};',
    '  window.pendo.onGuideSnoozed = function() {};',
    '}',
];

function getExecuteCodeBlockScripts (stepId) {
    return [
        'if (codeBlockContent) {',
        '  var scriptRegex = /<script([^>]*)>([\\s\\S]*?)<\\/script>/gi;',
        '  var match;',
        '  while ((match = scriptRegex.exec(codeBlockContent)) !== null) {',
        '    var attrs = match[1];',
        '    var content = match[2].trim();',
        '    if (!content) continue;',
        '    var hasType = attrs.indexOf("type=") !== -1;',
        '    var isJs = !hasType || attrs.indexOf("javascript") !== -1;',
        '    if (!isJs) continue;',
        '    content = content.replace(/<%[^=].*?%>/g, "");',
        '    content = content.replace(/<%=\\s*guide\\.id\\s*%>/g, guideId);',
        `    content = content.replace(/<%=\\s*step\\.id\\s*%>/g, ${JSON.stringify(stepId)});`,
        '    try {',
        '      var liveScript = document.createElement("script");',
        '      liveScript.setAttribute("data-dynamic", "true");',
        '      liveScript.textContent = content;',
        '      document.body.appendChild(liveScript);',
        '    } catch(scriptErr) {',
        '      console.error("[Sandbox] Script execution error:", scriptErr);',
        '    }',
        '  }',
        '}',
    ];
}

function getMessageListener (stepId) {
    return [
        'window.addEventListener("message", function(event) {',
        '  if (!event.data) return;',
        '  if (event.data.type === "heartbeat") {',
        `    window.parent.postMessage({ type: "heartbeat-ack", stepId: ${stepId} }, "*");`,
        '    return;',
        '  }',
        '  if (event.data.type !== "code-block-update") return;',
        '  var dynamicScripts = document.querySelectorAll("body > script[data-dynamic]");',
        '  dynamicScripts.forEach(function(s) { document.body.removeChild(s); });',
        '  var bootstrapScripts = document.querySelectorAll("body > script");',
        '  var children = Array.prototype.slice.call(document.body.children);',
        '  children.forEach(function(child) {',
        '    var isScript = false;',
        '    for (var i = 0; i < bootstrapScripts.length; i++) {',
        '      if (child === bootstrapScripts[i]) { isScript = true; break; }',
        '    }',
        '    if (!isScript) document.body.removeChild(child);',
        '  });',
        '  var oldStyles = document.head.querySelectorAll("style[data-code-block-css]");',
        '  oldStyles.forEach(function(s) { document.head.removeChild(s); });',
        '  codeBlockContent = event.data.html || "";',
        '  codeBlockCss = event.data.css || "";',
        '  step.getContent = function() { return codeBlockContent; };',
        '  renderGuide();',
        '});',
    ];
}

function getResizeObserver (stepId) {
    return [
        'var resizeTimeout;',
        'var firstResize = true;',
        'var resizeObserver = new ResizeObserver(function() {',
        '  if (firstResize) {',
        '    firstResize = false;',
        '    var height = document.body.scrollHeight;',
        `    window.parent.postMessage({ type: "resize", height: height, stepId: ${stepId} }, "*");`,
        '    return;',
        '  }',
        '  clearTimeout(resizeTimeout);',
        '  resizeTimeout = setTimeout(function() {',
        '    var height = document.body.scrollHeight;',
        `    window.parent.postMessage({ type: "resize", height: height, stepId: ${stepId} }, "*");`,
        '  }, 150);',
        '});',
        'resizeObserver.observe(document.body);',
    ];
}

export function buildRenderScript ({ domJson, stepId, guideId, codeBlockContent, codeBlockCss }) {
    const dataVars = getDataVars(domJson, codeBlockContent, codeBlockCss);
    const mockGlobals = getMockGlobals(stepId, guideId);
    const executeCodeBlockScripts = getExecuteCodeBlockScripts(stepId);

    const renderGuideBody = [
        ...mockGlobals,
        '',
        ...renderDom,
        '',
        ...overridePositioning,
        '',
        ...patchPendoApi,
        '',
        ...executeCodeBlockScripts,
    ].map((line) => (line ? `    ${line}` : '')).join('\n');

    return [
        ...dataVars,
        '',
        'function renderGuide() {',
        '  try {',
        '    if (!window.pendo || !window.pendo.buildNodeFromJSON) return;',
        '',
        renderGuideBody,
        '  } catch(err) {',
        '    console.error("[Sandbox] Render error:", err);',
        '  }',
        '}',
        '',
        'if (document.readyState === "complete") {',
        '  setTimeout(renderGuide, 0);',
        '} else {',
        '  window.addEventListener("load", renderGuide);',
        '}',
        '',
        ...getMessageListener(JSON.stringify(stepId)),
        '',
        ...getResizeObserver(JSON.stringify(stepId)),
    ].join('\n');
}
// end scripts used to render code block previews in app